/** Ponto de uma medida do problema. A série não se edita: corrigir é apagar e incluir. */
export type MeasurePointKind = 'baseline' | 'target' | 'actual' | 'after';

export interface MeasurePoint {
  pointKind: MeasurePointKind;
  value: number;
  occurredOn: string;
  createdAt: string;
}

export interface MeasuredAmount {
  measureId: string;
  unit: string;
  amount: number;
}

/** Mais recente por data do fato e, no empate, pelo instante de gravação. */
export function compareMeasurePoints(left: MeasurePoint, right: MeasurePoint): number {
  if (left.occurredOn !== right.occurredOn) return left.occurredOn < right.occurredOn ? 1 : -1;
  if (left.createdAt !== right.createdAt) return left.createdAt < right.createdAt ? 1 : -1;
  return 0;
}

/** Autoridade única da leitura. A meta corrente é `currentMeasurePoint(points, 'target')`. */
export function currentMeasurePoint<T extends MeasurePoint>(
  points: readonly T[],
  kind: MeasurePointKind,
): T | null {
  const matches = points.filter((point) => point.pointKind === kind);
  if (matches.length === 0) return null;
  return [...matches].sort(compareMeasurePoints)[0];
}

export function currentTarget<T extends MeasurePoint>(points: readonly T[]): T | null {
  return currentMeasurePoint(points, 'target');
}

export type MeasureRole = 'loss' | 'control' | 'comparison' | 'secondary';
export type MeasureDirection = 'higher_is_better' | 'lower_is_better' | 'target_is_best';

export interface IssueMeasureView {
  id: string;
  role: MeasureRole;
  name: string;
  unit: string;
  direction: MeasureDirection;
  points: readonly MeasurePoint[];
}

export interface LossView {
  id: string;
  name: string;
  unit: string;
  value: number | null;
}

export interface ControlView {
  id: string;
  name: string;
  unit: string;
  direction: MeasureDirection;
  baseline: number | null;
  target: number | null;
}

/** Valor exibido da perda: o actual corrente. A meta não entra aqui. */
export function presentLosses(measures: readonly IssueMeasureView[]): LossView[] {
  return measures.filter((measure) => measure.role === 'loss').map((measure) => ({
    id: measure.id,
    name: measure.name,
    unit: measure.unit,
    value: currentMeasurePoint(measure.points, 'actual')?.value ?? null,
  }));
}

function controlView(measure: IssueMeasureView): ControlView {
  return {
    id: measure.id,
    name: measure.name,
    unit: measure.unit,
    direction: measure.direction,
    baseline: currentMeasurePoint(measure.points, 'baseline')?.value ?? null,
    target: currentTarget(measure.points)?.value ?? null,
  };
}

/** Baseline e meta de cada item de controle. A meta é `currentTarget`, sem outra regra. */
export function presentControls(measures: readonly IssueMeasureView[]): ControlView[] {
  return measures.filter((measure) => measure.role === 'control').map(controlView);
}

export function presentControl(measures: readonly IssueMeasureView[]): ControlView | null {
  return presentControls(measures)[0] ?? null;
}

export interface MeasureComparison {
  id: string;
  name: string;
  unit: string;
  direction: MeasureDirection;
  before: number | null;
  target: number | null;
  after: number | null;
  result: string;
}

/** Antes é baseline; se não houver, o actual da observação. Depois é só `after`. */
export function presentMeasureComparison(measure: IssueMeasureView): MeasureComparison {
  const before = currentMeasurePoint(measure.points, 'baseline')?.value
    ?? currentMeasurePoint(measure.points, 'actual')?.value
    ?? null;
  const target = currentMeasurePoint(measure.points, 'target')?.value ?? null;
  const after = currentMeasurePoint(measure.points, 'after')?.value ?? null;
  return {
    id: measure.id,
    name: measure.name,
    unit: measure.unit,
    direction: measure.direction,
    before,
    target,
    after,
    result: measureEffect(measure.direction, before, target, after),
  };
}

export function formatMeasureValue(value: number | null): string {
  if (value == null || !Number.isFinite(value)) return '—';
  return new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 4 }).format(value);
}

function measureEffect(
  direction: MeasureDirection,
  before: number | null,
  target: number | null,
  after: number | null,
): string {
  if (after == null) return '—';
  if (direction === 'target_is_best') {
    if (target == null) return '—';
    return after === target ? 'Na meta' : 'Fora da meta';
  }
  if (target != null) {
    const met = direction === 'lower_is_better' ? after <= target : after >= target;
    return met ? 'Dentro da meta' : 'Fora da meta';
  }
  if (before == null) return '—';
  if (after === before) return 'Igual';
  if (direction === 'lower_is_better') return after < before ? 'Reduziu' : 'Aumentou';
  return after > before ? 'Aumentou' : 'Reduziu';
}

/** Recusa soma entre medidas ou unidades diferentes. O banco não faz essa conta. */
export function sumMeasuredAmounts(amounts: readonly MeasuredAmount[]): number {
  if (amounts.length === 0) return 0;
  const [first] = amounts;
  const mixed = amounts.some((item) => item.measureId !== first.measureId || item.unit !== first.unit);
  if (mixed) throw new Error('Não some valores de medidas ou unidades diferentes.');
  return amounts.reduce((total, item) => total + item.amount, 0);
}
